import { DialogService, ResponseMessage, ResponseStatus } from '@sdbn/common';
import { Component, Input, Output, OnInit, EventEmitter } from '@angular/core';
import { MovementOrderDetails, MovementOrderProcessingStatus, MovementOrderStrategy, MovementOrderTargetStationStrategy, MovementOrderVehicleStrategy, ProcessingStatusChange } from '../../movement-orders.model';
import { MovementOrdersService } from '../../movement-orders.service';
import { MovementOrderStrategyDialogComponent } from '../movement-order-strategy-dialog/movement-order-strategy-dialog.component';
import { TranslationService } from '@sdbn/common';
import { MovementOrderReroutingDialogComponent } from '../movement-order-rerouting-dialog/movement-order-rerouting-dialog.component';


@Component({
    selector: 'app-movement-order-details',
    templateUrl: './movement-order-details.component.html',
    styleUrls: ['./movement-order-details.component.scss'],
    standalone: false
})
export class MovementOrderDetailsComponent implements OnInit {

  responseMessages = new Array<ResponseMessage>();

  movementOrderDetails: MovementOrderDetails;
  serviceTruckId: number;

  statusChangeButtonEnabled = false;
  statusChangeButtonVisible = true;
  statusChangeValue: MovementOrderProcessingStatus;
  statusChangeLabel = 'Rangierauftrag annehmen';

  statusChangeReverseButtonEnabled = false;
  statusChangeReverseValue: MovementOrderProcessingStatus;

  cancelEnabled = false;

  loading = false;

  @Input('serviceTruckId') set setServiceTruckId(serviceTruckId: number) {
    this.serviceTruckId = serviceTruckId;
    this.updateStatusChangeAction();
    this.updateCancelStatus();
  }

  @Input('movementOrderDetailsId') set setMovementOrderDetailsId(movementOrderDetailsId: number) {
    if (movementOrderDetailsId != null) {
      this.movementOrderDetails = null;
      this.movementOrdersService.loadMovementOrderDetails(movementOrderDetailsId)
        .subscribe(
          details => {
            this.movementOrderDetails = details;
            this.updateStatusChangeAction();
            this.updateStatusChangeReverseAction();
            this.updateCancelStatus();
          }
        );
    } else {
      this.movementOrderDetails = null;
      this.updateStatusChangeAction();
      this.updateStatusChangeReverseAction();
      this.updateCancelStatus();
    }
  }

  @Output() detailsChanged = new EventEmitter<void>();

    constructor(
    public translationService: TranslationService,
    private dialogService: DialogService,
    private movementOrdersService: MovementOrdersService) { }

  ngOnInit(): void {
  }

  public cancel() {
    this.loading = true;
    this.movementOrdersService.cancelMovingOrder(this.movementOrderDetails.id).subscribe(
      response => {
        if (response.status !== ResponseStatus.SUCCESS) {
          console.log('Fehler bei Storno.', response);
        }
        this.detailsChanged.next();
      },
      error => {
        console.log(error);
        this.loading = false;
      }
    );
    this.loading = false;
  }

  public handleStatusChange() {

    if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.OPEN
      && (this.movementOrderDetails.targetStationStrategy !== MovementOrderTargetStationStrategy.FIXED || this.movementOrderDetails.vehicleStrategy !== MovementOrderVehicleStrategy.FIXED)) {

      if (this.movementOrderDetails.reroutingNeeded) {
        this.performStatusChangeWithRerouting();
      } else {
        this.performStatusChangeWithStrategySelection();
      }
    } else {
      const request: ProcessingStatusChange = {
        movementOrderId: this.movementOrderDetails.id,
        processingStatus: this.statusChangeValue,
        serviceTruckId: this.serviceTruckId,
        vehicleId: null,
        targetStationId: null,
        reroutingTargetStationId: null
      };
      this.performStatusChange(request);
    }
  }

  private performStatusChangeWithRerouting() {
    this.dialogService.openLarge(MovementOrderReroutingDialogComponent, {
      movementOrderDetails: this.movementOrderDetails
    }).afterClosed().subscribe(
      reroutingTargetStationId => {
        if (reroutingTargetStationId != null) {
          const request: ProcessingStatusChange = {
            movementOrderId: this.movementOrderDetails.id,
            processingStatus: this.statusChangeValue,
            serviceTruckId: this.serviceTruckId,
            vehicleId: null,
            targetStationId: null,
            reroutingTargetStationId: reroutingTargetStationId
          };
          this.performStatusChange(request);
        }
      }
    );
  }

  private performStatusChangeWithStrategySelection() {
    this.dialogService.openLarge(MovementOrderStrategyDialogComponent, {
      movementOrderDetails: this.movementOrderDetails
    }).afterClosed().subscribe(
      result => {
        if (result != null) {
          const strat = result as MovementOrderStrategy;
          const request: ProcessingStatusChange = {
            movementOrderId: this.movementOrderDetails.id,
            processingStatus: this.statusChangeValue,
            serviceTruckId: this.serviceTruckId,
            vehicleId: strat.vehicleId,
            targetStationId: strat.targetStationId,
            reroutingTargetStationId: null
          };
          this.performStatusChange(request);
        }
      }
    );
  }

  private performStatusChange(request: ProcessingStatusChange) {
    this.loading = true;
    this.responseMessages = new Array<ResponseMessage>();
    this.movementOrdersService.handleStatusChange(request).subscribe(
      response => {
        if (response.status === ResponseStatus.SUCCESS) {
          this.detailsChanged.next();
        } else {
          this.responseMessages = response.responseMessages;
          console.log('Fehler bei Statusänderung!');
        }
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      }
    );
  }

  public handleStatusChangeReverse() {
    this.responseMessages = new Array<ResponseMessage>();
    this.movementOrdersService.handleStatusChangeReverse(this.movementOrderDetails.id, this.statusChangeReverseValue).subscribe(
      response => {
        if (response.status === ResponseStatus.SUCCESS) {
          this.detailsChanged.next();
        } else {
          this.responseMessages = response.responseMessages;
          console.log('Fehler bei Statusänderung!');
        }
      },
      error => {
        console.log(error);
        this.loading = false;
      }
    );
  }

  private updateStatusChangeReverseAction() {
    if (this.movementOrderDetails != null) {

      this.statusChangeReverseButtonEnabled = true;

      if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.OPEN) {
        this.statusChangeReverseButtonEnabled = false;
      } else if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.STARTED) {
        this.statusChangeReverseValue = MovementOrderProcessingStatus.OPEN;
      } else if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.MOVEMENT_STARTED) {
        this.statusChangeReverseValue = MovementOrderProcessingStatus.STARTED;
      } else if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.MOVEMENT_COMPLETED) {
        this.statusChangeReverseValue = MovementOrderProcessingStatus.MOVEMENT_STARTED;
      } else if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.COMPLETED) {
        this.statusChangeReverseValue = MovementOrderProcessingStatus.MOVEMENT_COMPLETED;
      }
      else {
        this.statusChangeReverseButtonEnabled = false;
      }

    } else {
      this.statusChangeReverseButtonEnabled = false;
    }
  }

  private updateStatusChangeAction() {
    if (this.movementOrderDetails != null) {

      this.statusChangeButtonEnabled = true;
      this.statusChangeButtonVisible = true;

      if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.OPEN) {
        this.statusChangeButtonEnabled = this.serviceTruckId != null;
        this.statusChangeLabel = 'tmc-yard.ui.movement-orders-details.status-change-label.start'; //'Rangierauftrag annehmen';
        this.statusChangeValue = MovementOrderProcessingStatus.STARTED;
      } else if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.STARTED) {
        this.statusChangeLabel = 'tmc-yard.ui.movement-orders-details.status-change-label.start-movment-order'; //'Wechselbrücke aufgenommen'; // Fahrzeug angehängt
        this.statusChangeValue = MovementOrderProcessingStatus.MOVEMENT_STARTED;
      } else if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.MOVEMENT_STARTED) {
        this.statusChangeLabel = 'tmc-yard.ui.movement-orders-details.status-change-label.complete-movement-order'; //'Wechselbrücke abgestellt'; // Fahrzeug abgestellt
        this.statusChangeValue = MovementOrderProcessingStatus.MOVEMENT_COMPLETED;
      } else if (this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.MOVEMENT_COMPLETED) {
        this.statusChangeLabel = 'tmc-yard.ui.movement-orders-details.status-change-label.complete'; //'Vorgang abschließen'; // Abgeschlossen
        this.statusChangeValue = MovementOrderProcessingStatus.COMPLETED;
      } else {
        this.statusChangeButtonEnabled = false;
        this.statusChangeButtonVisible = false;
      }
    } else {
      this.statusChangeButtonEnabled = false;
      this.statusChangeButtonVisible = false;
    }
  }

  private updateCancelStatus() {
    if (this.movementOrderDetails != null) {
      this.cancelEnabled = this.movementOrderDetails.processingStatus === MovementOrderProcessingStatus.OPEN;
    }
  }
}
